/**
 * Pure helpers for the PRD-6.1 share model (SE-8313): the WHO HAS ACCESS
 * connection kept in the Apollo cache, the role vocabulary shown to owners,
 * and the invite resolution that decides whether an Invite press sends a
 * mutation or shows an inline error.
 * @module features/scouting/utils/shareWatchlistGrants
 */

import {
  UserWatchlistRole,
  type WatchlistListUserWatchlistsByWatchlistIdQuery,
  type WatchlistUserWatchlistFragment,
} from "@/generated/graphql";

/** The cached grants query the dialog reads and every write updates. */
type MembersQuery = WatchlistListUserWatchlistsByWatchlistIdQuery;
/** One edge of that connection. */
type MemberEdge =
  MembersQuery["listUserWatchlistsByWatchlistId"]["edges"][number];

/** Roles an owner can grant. OWNER is never assignable through the dialog. */
export type ShareGrantRole =
  | UserWatchlistRole.Editor
  | UserWatchlistRole.Viewer;

/** A person the owner can invite. */
export interface ShareCandidateItem {
  /** User id. */
  readonly userId: string;
  /** Resolved display name. */
  readonly name: string;
}

/**
 * Whether the WHO HAS ACCESS rows are known: "loading" until the grants read
 * has answered once, "error" when it failed with nothing cached, else
 * "ready". Only "ready" may be read as "nobody else has access".
 */
export type ShareAccessState = "loading" | "error" | "ready";

/**
 * Derives the access state from the grants read.
 * @param read - What the grants query currently holds
 * @param read.hasData - Whether a result (cached or fresh) exists
 * @param read.hasError - Whether the read failed
 * @returns "ready" with data, else "error" on failure, else "loading".
 */
export const shareAccessStateFor = (read: {
  readonly hasData: boolean;
  readonly hasError: boolean;
}): ShareAccessState => {
  if (read.hasData) return "ready";
  return read.hasError ? "error" : "loading";
};

/**
 * Folds the access states of several reads into the one the surface shows:
 * a failed read wins over one still loading, which wins over one that
 * answered — so "ready" (and with it "nobody else has access") is published
 * only once EVERY read has answered (SE-8359: the group read joins the
 * direct grants read).
 * @param states - The per-read states
 * @returns The combined state
 */
export const combineShareAccessStates = (
  ...states: readonly ShareAccessState[]
): ShareAccessState => {
  if (states.includes("error")) return "error";
  if (states.includes("loading")) return "loading";
  return "ready";
};

/** Copy shown in place of the empty state while the rows are not known. */
export const SHARE_ACCESS_STATE_COPY: Readonly<
  Record<Exclude<ShareAccessState, "ready">, string>
> = {
  loading: "Loading who has access…",
  error: "Couldn't load who has access.",
};

/** One WHO HAS ACCESS row. */
export interface ShareAccessRowItem {
  /** Grant (UserWatchlist row) id — the role-change and revoke target. */
  readonly userWatchlistId: string;
  /** User id. */
  readonly userId: string;
  /** Resolved display name. */
  readonly name: string;
  /** Current role on the list. */
  readonly role: UserWatchlistRole;
  /** Whether a role change or revoke for this row is in flight. */
  readonly isPending: boolean;
  /**
   * "Also editor via Everyone at {organization}" when the group grant
   * outranks this person's direct role (SE-8359, R06 §3); null otherwise.
   */
  readonly caption?: string | null;
  /**
   * Whether the membership behind the grant is not ACTIVE (SE-8359, R06 §5):
   * the grant is kept and listed, ineffective, with no role control.
   */
  readonly isInactive?: boolean;
}

/** Select options for the invite row and each access row. */
export const SHARE_ROLE_OPTIONS: readonly {
  readonly value: ShareGrantRole;
  readonly label: string;
}[] = [
  { value: UserWatchlistRole.Editor, label: "Can edit" },
  { value: UserWatchlistRole.Viewer, label: "Can view" },
];

/**
 * Narrows a Select value to a grantable role.
 * @param value - Raw select value
 * @returns `true` for EDITOR or VIEWER.
 */
export const isShareGrantRole = (value: string): value is ShareGrantRole =>
  value === UserWatchlistRole.Editor || value === UserWatchlistRole.Viewer;

/**
 * Human label for a role in the access list.
 * @param role - Grant role
 * @returns "Owner", "Editor" or "Viewer".
 */
export const shareRoleLabel = (role: UserWatchlistRole): string => {
  if (role === UserWatchlistRole.Owner) return "Owner";
  return role === UserWatchlistRole.Editor ? "Editor" : "Viewer";
};

/**
 * Label of the grantable role as shown in the invite Select.
 * @param role - Grant role
 * @returns "Can edit" or "Can view".
 */
export const shareRoleOptionLabel = (role: ShareGrantRole): string =>
  SHARE_ROLE_OPTIONS.find(option => option.value === role)?.label ?? "Can view";

/**
 * Appends a freshly created grant to the cached members connection.
 * @param previous - Cached query result, or undefined when nothing is cached
 * @param grant - The grant returned by `createUserWatchlist`
 * @returns The next cache value, or undefined to leave the cache untouched.
 */
export const appendGrant = (
  previous: MembersQuery | null | undefined,
  grant: WatchlistUserWatchlistFragment
): MembersQuery | undefined => {
  if (!previous) return undefined;
  const connection = previous.listUserWatchlistsByWatchlistId;
  if (connection.edges.some(edge => edge.node.id === grant.id)) return previous;
  const edge: MemberEdge = {
    __typename: "UserWatchlistEdge",
    cursor: grant.id,
    node: grant,
  };
  return {
    ...previous,
    listUserWatchlistsByWatchlistId: {
      ...connection,
      edges: [...connection.edges, edge],
    },
  };
};

/**
 * Removes a revoked grant from the cached members connection.
 * @param previous - Cached query result, or undefined when nothing is cached
 * @param userWatchlistId - The revoked grant id
 * @returns The next cache value, or undefined to leave the cache untouched.
 */
export const removeGrant = (
  previous: MembersQuery | null | undefined,
  userWatchlistId: string
): MembersQuery | undefined => {
  if (!previous) return undefined;
  const connection = previous.listUserWatchlistsByWatchlistId;
  const edges = connection.edges.filter(
    edge => edge.node.id !== userWatchlistId
  );
  if (edges.length === connection.edges.length) return previous;
  return {
    ...previous,
    listUserWatchlistsByWatchlistId: { ...connection, edges },
  };
};

/** Inputs for {@link resolveInvitee}. */
export interface ResolveInviteeInput {
  /** The suggestion the owner picked, if any. */
  readonly selected: ShareCandidateItem | null;
  /** Raw search text. */
  readonly query: string;
  /** Search-filtered people who could still be invited. */
  readonly candidates: readonly ShareCandidateItem[];
  /** Everyone in the organization, including people who already have access. */
  readonly organizationUsers: readonly ShareCandidateItem[];
  /** User ids that already hold a grant (owner included). */
  readonly memberUserIds: ReadonlySet<string>;
  /** The signed-in owner. */
  readonly currentUserId: string | undefined;
  /** Noun in the "already have access" copy; "list" unless the caller says. */
  readonly entityNoun?: string;
}

/** Outcome of an Invite press before any mutation is sent. */
export type InviteResolution =
  | { readonly candidate: ShareCandidateItem; readonly error: null }
  | { readonly candidate: null; readonly error: string };

const invalid = (error: string): InviteResolution => ({
  candidate: null,
  error,
});

/**
 * Decides who an Invite press targets. A picked suggestion wins; otherwise the
 * typed text must name exactly one person who does not already have access.
 * @param input - Selection, search text and the people it can match
 * @param input.selected - The suggestion the owner picked, if any
 * @param input.query - Raw search text
 * @param input.candidates - Search-filtered people who could still be invited
 * @param input.organizationUsers - Everyone in the organization
 * @param input.memberUserIds - User ids that already hold a grant
 * @param input.currentUserId - The signed-in owner
 * @param input.entityNoun - Noun in the "already have access" copy
 * @returns The candidate to invite, or the inline error to show.
 */
export const resolveInvitee = ({
  selected,
  query,
  candidates,
  organizationUsers,
  memberUserIds,
  currentUserId,
  entityNoun = "list",
}: ResolveInviteeInput): InviteResolution => {
  if (selected) return { candidate: selected, error: null };
  const trimmed = query.trim();
  const needle = trimmed.toLowerCase();
  if (!needle) return invalid("Choose a person to invite.");
  const exact = organizationUsers.filter(
    user => user.name.toLowerCase() === needle
  );
  if (exact.some(user => user.userId === currentUserId)) {
    return invalid(`You already have access to this ${entityNoun}.`);
  }
  const alreadyShared = exact.find(user => memberUserIds.has(user.userId));
  if (alreadyShared) {
    return invalid(`${alreadyShared.name} already has access.`);
  }
  const target = exact.length === 1 ? exact[0] : undefined;
  if (target) return { candidate: target, error: null };
  if (candidates.length === 1) {
    return { candidate: candidates[0], error: null };
  }
  if (candidates.length > 1) {
    return invalid("Pick one person from the suggestions.");
  }
  // SE-8415: an email-shaped query gets the frame's sentence; a name keeps
  // the name form.
  if (trimmed.includes("@")) {
    return invalid("No one at your club with that email");
  }
  return invalid(`No one named "${trimmed}" is in your organization.`);
};
